import { useCallback, useMemo, useState } from 'react';

/** Blanks always sink to the bottom, whichever direction is active. */
const isBlank = (value) => value === null || value === undefined || value === '';

const compare = (a, b) => {
  if (isBlank(a) && isBlank(b)) return 0;
  if (isBlank(a)) return 1;
  if (isBlank(b)) return -1;

  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    return na - nb;
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Click-to-sort for the module's tables.
 *
 * A column sorts on `sortValue(row)` when it defines one, otherwise on its raw
 * `key`. Columns marked `sortable: false` (the action column) are skipped.
 * Clicking cycles ascending -> descending -> unsorted.
 */
const useTableSort = (rows, columns) => {
  const [sort, setSort] = useState({ key: null, dir: 'asc' });

  const toggle = useCallback((key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sort.key) return rows;

    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;

    const value = column.sortValue || ((row) => row[column.key]);
    const sign = sort.dir === 'asc' ? 1 : -1;

    // Slice first: the caller's array is state we must not reorder in place.
    return [...rows].sort((a, b) => sign * compare(value(a), value(b)));
  }, [rows, columns, sort]);

  const indicator = useCallback(
    (key) => (sort.key !== key ? '⇅' : sort.dir === 'asc' ? '▲' : '▼'),
    [sort]
  );

  return { sorted, sort, toggle, indicator };
};

export default useTableSort;

/** A header cell that sorts, unless the column opted out. */
export const sortableHeaderProps = (column, { toggle }) =>
  column.sortable === false
    ? {}
    : {
        onClick: () => toggle(column.key),
        style: { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
        title: `Sort by ${column.label}`,
      };
